// One line on sign-up, the paywall and /pricing for someone a creator
// invited: who, and that their free Voice Pass comes with the plan (it is
// granted on the first paid subscription invoice, lib/referral/server). The
// parent decides whether a plan is being started; this decides whether
// there's an invite to mention.
import React, { useEffect, useState } from "react";
import { Gift } from "lucide-react";
import { useT } from "@jammable/ui-core/src/i18n";
import { useAuth } from "../../context/AuthProvider";
import { readReferralCookie } from "../../lib/referral/code";
import { useReferrer } from "./useReferrer";
import { tSlot } from "./tSlot";

type Referral = {
  code?: string | null;
  referrerId?: string | null;
  rewardedAt?: string | null;
  blockedReason?: string | null;
};

// Signed in: the referral saved on the account at sign-up (the cookie no
// longer counts then). Signed out: the link they arrived on.
function useInviteCode() {
  const { user } = useAuth() as {
    user?: { firebaseUid?: string; referral?: Referral | null };
  };
  const [cookieCode, setCookieCode] = useState<string | null>(null);
  useEffect(() => {
    setCookieCode(readReferralCookie()?.code ?? null);
  }, []);
  if (!user?.firebaseUid) return cookieCode;
  const r = user.referral;
  if (!r?.referrerId || r.rewardedAt || r.blockedReason) return null;
  return r.code ?? null;
}

export default function InvitedLine({ dark = false }: { dark?: boolean }) {
  const t = useT();
  const referrer = useReferrer(useInviteCode());
  if (!referrer) return null;

  return (
    <div
      className={`tw-flex tw-items-center tw-gap-3 tw-rounded-[20px] tw-p-3 tw-text-left ${
        dark
          ? "tw-bg-jam-lime/[0.12] tw-text-white"
          : "tw-bg-jam-lav tw-text-jam-ink"
      }`}
    >
      <span className="tw-relative tw-flex-shrink-0">
        <span className="tw-block tw-h-10 tw-w-10 tw-overflow-hidden tw-rounded-full tw-bg-jam-purple tw-ring-2 tw-ring-jam-lime">
          {referrer.avatar && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={referrer.avatar}
              alt=""
              className="tw-h-full tw-w-full tw-object-cover"
            />
          )}
        </span>
        <span className="tw-absolute -tw-bottom-1 -tw-right-1 tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink">
          <Gift size={11} strokeWidth={3} />
        </span>
      </span>
      <span className="tw-flex tw-min-w-0 tw-flex-col">
        <span
          className={`tw-text-[14px] tw-font-bold md:tw-text-[15px] ${dark ? "tw-text-white/80" : "tw-text-jam-muted"}`}
        >
          {tSlot(
            t,
            "referral.line.title",
            "name",
            <b
              className={`tw-break-all tw-font-extrabold ${dark ? "tw-text-white" : "tw-text-jam-ink"}`}
            >
              @{referrer.username}
            </b>,
          )}
        </span>
        <span
          className={`tw-text-[13px] tw-font-extrabold md:tw-text-sm ${dark ? "tw-text-jam-lime" : "tw-text-jam-purple"}`}
        >
          {t("referral.line.detail")}
        </span>
      </span>
    </div>
  );
}
